import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Home.css";

export const A = "/remy";
const API = process.env.REACT_APP_BACKEND_URL;

const UserIcon = (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
  </svg>
);
const PhoneIcon = (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/* Waitlist invite form — First/Last/Phone + REQUIRED Terms/Privacy ack and OPTIONAL SMS consent.
   One shared component/pipeline for every public form. `labeled` renders visible field labels and
   `bareIds` emits exact ids (mobile_number / sms_consent / legal_ack) for the /sms-opt-in page. */
export function InviteForm({ idPrefix, variant = "light", source, labeled = false, bareIds = false }:
  { idPrefix: string; variant?: "light" | "card"; source: string; labeled?: boolean; bareIds?: boolean }) {
  const [done, setDone] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [mobile, setMobile] = useState("");
  const [legalAck, setLegalAck] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Field ids: bare (mobile_number) on the dedicated opt-in page, prefixed elsewhere for uniqueness.
  const fid = (n: string) => (bareIds ? n : `${idPrefix}-${n}`);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!legalAck) return; // only Terms/Privacy is required; SMS consent is voluntary
    setErr("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/waitlist/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ first_name: first, last_name: last, mobile_number: mobile, source, legal_ack: legalAck, sms_consent: smsConsent }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Something went wrong. Please try again.");
      }
      setDone(true);
    } catch (e: any) {
      setErr(e.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className={`rh-form rh-form--${variant}`} data-testid={`${idPrefix}-invite-done`}>
        <div className="rh-form__done">
          <img className="rh-form__done-script" src={`${A}/life-feels-easier.png`} alt="Life feels easier with Remy." />
          <p>You're on the list. We'll be in touch when your invite is ready.</p>
        </div>
      </div>
    );
  }

  return (
    <form className={`rh-form rh-form--${variant}`} onSubmit={submit} data-testid={`${idPrefix}-invite-form`} noValidate>
      {labeled ? (
        <div className="rh-optin-fields">
          <div className="rh-optin-field">
            <label htmlFor={fid("first_name")}>First name</label>
            <input id={fid("first_name")} name="first_name" autoComplete="given-name" value={first}
              onChange={(e) => setFirst(e.target.value)} required data-testid={`${idPrefix}-invite-first`} />
          </div>
          <div className="rh-optin-field">
            <label htmlFor={fid("last_name")}>Last name</label>
            <input id={fid("last_name")} name="last_name" autoComplete="family-name" value={last}
              onChange={(e) => setLast(e.target.value)} data-testid={`${idPrefix}-invite-last`} />
          </div>
          <div className="rh-optin-field">
            <label htmlFor={fid("mobile_number")}>Mobile phone number</label>
            <input id={fid("mobile_number")} name="mobile_number" type="tel" inputMode="tel" autoComplete="tel"
              value={mobile} onChange={(e) => setMobile(e.target.value)} required data-testid={`${idPrefix}-invite-mobile`} />
          </div>
        </div>
      ) : (
        <div className="rh-form__row">
          <label className="rh-input" htmlFor={fid("first_name")}>
            {UserIcon}
            <input id={fid("first_name")} name="first_name" autoComplete="given-name"
              placeholder="First name" value={first} onChange={(e) => setFirst(e.target.value)} required
              data-testid={`${idPrefix}-invite-first`} />
          </label>
          <label className="rh-input" htmlFor={fid("last_name")}>
            {UserIcon}
            <input id={fid("last_name")} name="last_name" autoComplete="family-name"
              placeholder="Last name" value={last} onChange={(e) => setLast(e.target.value)}
              data-testid={`${idPrefix}-invite-last`} />
          </label>
          <label className="rh-input" htmlFor={fid("mobile_number")}>
            {PhoneIcon}
            <input id={fid("mobile_number")} name="mobile_number" type="tel" inputMode="tel"
              autoComplete="tel" placeholder="Phone number" value={mobile}
              onChange={(e) => setMobile(e.target.value)} required
              data-testid={`${idPrefix}-invite-mobile`} />
          </label>
        </div>
      )}
      <label className="rh-check" htmlFor={fid("legal_ack")} data-testid={`${idPrefix}-invite-legal`}>
        <input id={fid("legal_ack")} name="legal_ack" type="checkbox" checked={legalAck} required
          onChange={(e) => setLegalAck(e.target.checked)} data-testid={`${idPrefix}-invite-legal-input`} />
        <span>I agree to the <Link to="/terms" target="_blank" rel="noreferrer">Terms of Service</Link> and <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>.</span>
      </label>
      <label className="rh-check" htmlFor={fid("sms_consent")} data-testid={`${idPrefix}-invite-sms`}>
        <input id={fid("sms_consent")} name="sms_consent" type="checkbox" checked={smsConsent}
          aria-describedby={fid("sms-consent-disclosure")}
          onChange={(e) => setSmsConsent(e.target.checked)} data-testid={`${idPrefix}-invite-sms-input`} />
        <span id={fid("sms-consent-disclosure")}><b>OPTIONAL</b> — I consent to receive recurring text messages from Remy related to my invite, account, conversations, reminders, and assistant services I request. Message frequency varies. Message and data rates may apply. Reply HELP for help and STOP to opt out.</span>
      </label>
      <p className="rh-optin-note" data-testid={`${idPrefix}-invite-sms-note`}>You can request access without agreeing to text messages. If you do not opt in here, you can choose to opt in later during Remy account setup.</p>
      <button type="submit" className="rh-btn rh-btn--block" disabled={busy || !legalAck} data-testid={`${idPrefix}-invite-submit`}>
        {busy ? "Sending…" : <>Request an invite <span aria-hidden>→</span></>}
      </button>
      {err && <p className="rh-form__err" data-testid={`${idPrefix}-invite-error`}>{err}</p>}
      {variant === "light" && (
        <p className="rh-invite-note">Invite-only. We're opening access in small waves.</p>
      )}
    </form>
  );
}

/* Website graphic message bubble — green sent, white received. */
export const Bubble = ({ who, children }: { who: "me" | "remy"; children: React.ReactNode }) => (
  <div className={`rh-bub rh-bub--${who}`}>
    {who === "remy" && <img className="rh-bub__face" src={`${A}/icon.png`} alt="" aria-hidden />}
    <div className="rh-bub__body">{children}</div>
  </div>
);

/* Shared public navigation. Sign-in lives in the footer only (not the header). */
export function SiteNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const cls = (p: string) => (pathname === p ? "is-active" : undefined);
  // Pages that contain an in-page #invite section; elsewhere send visitors to the homepage invite.
  const hasInvite = ["/", "/how-it-works", "/what-remy-can-do"].includes(pathname);
  const inviteHref = hasInvite ? "#invite" : "/#invite";

  // Scroll to #invite when a page loads with that hash (e.g. arriving from the Contact page CTA).
  useEffect(() => {
    if (window.location.hash === "#invite") {
      const el = document.getElementById("invite");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, [pathname]);

  return (
    <nav className="rh-nav">
      <div className="rh-wrap rh-nav__inner">
        <Link to="/" className="rh-brand" data-testid="nav-brand">
          <img className="rh-brand__icon" src={`${A}/icon.png`} alt="" aria-hidden />
          <img className="rh-brand__word" src={`${A}/wordmark.png`} alt="Remy" />
        </Link>
        <div className="rh-nav__links">
          <Link to="/how-it-works" className={cls("/how-it-works")} data-testid="nav-how">How it works</Link>
          <Link to="/what-remy-can-do" className={cls("/what-remy-can-do")} data-testid="nav-what">What Remy can do</Link>
          <Link to="/privacy" className={cls("/privacy")} data-testid="nav-privacy">Privacy</Link>
        </div>
        <div className="rh-nav__cta">
          <a href={inviteHref} className="rh-btn" data-testid="nav-request-invite">Request an invite</a>
          <button className="rh-burger" aria-label="Menu" aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)} data-testid="nav-burger">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="rh-mobile-links" data-testid="nav-mobile-links">
          <Link to="/how-it-works" onClick={() => setMenuOpen(false)}>How it works</Link>
          <Link to="/what-remy-can-do" onClick={() => setMenuOpen(false)}>What Remy can do</Link>
          <Link to="/privacy" onClick={() => setMenuOpen(false)}>Privacy</Link>
          <a href={inviteHref} onClick={() => setMenuOpen(false)}>Request an invite</a>
        </div>
      )}
    </nav>
  );
}

/* Shared public footer. Sign-in lives here. */
export function SiteFooter() {
  return (
    <footer className="rh-footer">
      <div className="rh-wrap rh-footer__grid">
        <Link to="/" className="rh-brand">
          <img className="rh-brand__icon" src={`${A}/icon.png`} alt="" aria-hidden />
          <img className="rh-brand__word" src={`${A}/wordmark.png`} alt="Remy" />
        </Link>
        <div className="rh-footer__links">
          <Link to="/how-it-works">How it works</Link>
          <Link to="/what-remy-can-do">What Remy can do</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/login">Sign in</Link>
        </div>
        <img className="rh-footer__sign" src={`${A}/life-feels-easier.png`} alt="Life feels easier with Remy." />
      </div>
    </footer>
  );
}

/* Per-page document title + meta description (lightweight SEO). */
export function useSeo(title: string, description: string) {
  if (typeof document !== "undefined") {
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", description);
  }
}
